// DataTable.jsx
import React, { useEffect, useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import "./Dashboard.css";
import Select from "react-select";

export default function DataTable({
  columns,
  visibleColumns,
  toggleColumn,
  showColumnSelector,
  setShowColumnSelector,
  columnWidths,
  startResize,
  filters,
  handleClearFilter,
  handleChange,
  sliderRange,
  handleQuantitySlider,
  data,
  page,
  total,
  pageSize,
  setPage,
  handleProjectClick,
  handleCorporateBeneficiaryClick,
  handleIntermediaryClick,
  tableRef,
  options,
  sortBy,
  sortDir,
  handleSort,
}) {
  // ===== Quantity (slider + numeric inputs) helpers =====
  const Q_MIN = 0;
  const Q_MAX = 1_000_000;

  const formatNumber = (n) =>
    typeof n === "number" && Number.isFinite(n) ? n.toLocaleString() : "";

  // Local input states so users can freely type (including empty, commas, etc.)
  const [minInput, setMinInput] = useState(formatNumber(sliderRange?.[0] ?? Q_MIN));
  const [maxInput, setMaxInput] = useState(formatNumber(sliderRange?.[1] ?? Q_MAX));

  // Keep inputs in sync when slider changes from the outside
  useEffect(() => {
    if (Array.isArray(sliderRange) && sliderRange.length === 2) {
      setMinInput(formatNumber(sliderRange[0]));
      setMaxInput(formatNumber(sliderRange[1]));
    }
  }, [sliderRange]);

  const parseIntLoose = (v) => {
    if (v == null) return null;
    const digits = String(v).replace(/[^\d]/g, "");
    if (!digits) return null;
    const n = Number(digits);
    return Number.isFinite(n) ? n : null;
  };

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  const commitMin = () => {
    const parsed = parseIntLoose(minInput);
    if (parsed == null) {
      // Revert to current slider min if nothing valid
      setMinInput(formatNumber(sliderRange[0]));
      return;
    }
    const [, currMax] = sliderRange;
    const nextMin = clamp(parsed, Q_MIN, currMax);
    handleQuantitySlider([nextMin, currMax]);
    // inputs will re-sync via useEffect
  };

  const commitMax = () => {
    const parsed = parseIntLoose(maxInput);
    if (parsed == null) {
      setMaxInput(formatNumber(sliderRange[1]));
      return;
    }
    const [currMin] = sliderRange;
    const nextMax = clamp(parsed, Math.max(currMin, Q_MIN), Q_MAX);
    handleQuantitySlider([currMin, nextMax]);
  };

  const handleSliderChange = (next) => {
    handleQuantitySlider(next);
    // inputs sync via useEffect
  };

  const clearQuantity = () => {
    handleQuantitySlider([Q_MIN, Q_MAX]);
  };

  return (
    <>

      {/* Table */}
      <div className="dashboard-table-container">
        <table className="dashboard-table" ref={tableRef}>

        {/*------------------ */}
        {/* Tabke header */}
        <thead>
          <tr>
            {columns
              .filter((col) => {
                const colKey = typeof col === 'string' ? col : col.key;
                return visibleColumns.includes(colKey);
              })
              .map((col, index) => {
                const colKey = typeof col === 'string' ? col : col.key;
                const colLabel = typeof col === 'string' ? col.replace(/_/g, " ") : col.label;
                const isSorted = sortBy === colKey;
                const arrow = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "";
                
                return (
                  <th
                    key={colKey}
                    className="dashboard-th resizable-header"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      width:
                        colKey === "quantity_retired"
                          ? Math.max(columnWidths[colKey] ?? 200, 320)
                          : columnWidths[colKey],
                      minWidth: colKey === "quantity_retired" ? 320 : undefined,
                    }}
                    onClick={(e) => {
                      handleSort(colKey);
                    }}
                  >
                    <div className="header-content" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{colLabel}</span>
                      {arrow && (
                        <span style={{ fontSize: 11, opacity: 0.8 }}>{arrow}</span>
                      )}
                    </div>
                    {index < columns.filter((c) => visibleColumns.includes(typeof c === 'string' ? c : c.key)).length - 1 && (
                      <div
                        className="resize-handle"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          startResize(e, colKey);
                        }}
                      />
                    )}
                  </th>
                );
              })}
          </tr>

          {/*------------------ */}
          {/* Filter row */}
          <tr className="dashboard-filter-row">
            {columns
              .filter((col) => {
                const colKey = typeof col === 'string' ? col : col.key;
                return visibleColumns.includes(colKey);
              })
              .map((col) => {
                const colKey = typeof col === 'string' ? col : col.key;
                
                return (
                  <th
                    key={colKey + "_f"}
                    className="dashboard-th dashboard-filter-th"
                    style={{
                      width: colKey === "quantity_retired"
                        ? Math.max(columnWidths[colKey] ?? 200, 320)
                        : columnWidths[colKey],
                      minWidth: colKey === "quantity_retired" ? 320 : undefined,
                    }}
                  >
                    {/* Date-range filters */}
                    {colKey === "issuance_date" || colKey === "retirement_date" ? (
                      <div className="dashboard-date-filter-container">
                        {["start", "end"].map((end) => (
                          <div key={end} className="dashboard-date-filter-row">
                            <span className="dashboard-date-label">
                              {end === "start" ? "From" : "To"}
                            </span>
                            <div className="dashboard-filter-input-container">
                              <input
                                type="date"
                                name={`${colKey}_${end}`}
                                value={filters[`${colKey}_${end}`]}
                                onChange={handleChange}
                                className="dashboard-filter-input dashboard-date-input"
                              />
                              {filters[`${colKey}_${end}`] && (
                                <span
                                  onClick={() => handleClearFilter(`${colKey}_${end}`)}
                                  className="dashboard-filter-clear"
                                >
                                  ×
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                    // QUANTITY FILTER
                    ) : colKey === "quantity_retired" ? (
                      <div style={{ padding: "0 12px", display: "grid", gap: 6 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <label style={{ color: "white", fontSize: 12, width: 32 }}>
                            Min
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            value={minInput}
                            onChange={(e) => setMinInput(e.target.value)}
                            onBlur={commitMin}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitMin();
                            }}
                            className="dashboard-filter-input"
                            style={{
                              minWidth: 50,
                              maxWidth: 125,
                            }}
                            placeholder="0"
                          />
                          <label style={{ color: "white", fontSize: 12, width: 32 }}>
                            Max
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            value={maxInput}
                            onChange={(e) => setMaxInput(e.target.value)}
                            onBlur={commitMax}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitMax();
                            }}
                            className="dashboard-filter-input"
                            style={{
                              minWidth:90,
                              maxWidth: 125,
                            }}
                            placeholder={Q_MAX.toLocaleString()}
                          />

                          <button
                            onClick={clearQuantity}
                            className="dashboard-page-btn"
                            style={{ marginLeft: "auto" }}
                            title="Clear range"
                          >
                            Clear
                          </button>
                        </div>

                        <Slider
                          range
                          min={Q_MIN}
                          max={Q_MAX}
                          step={1000}
                          allowCross={false}
                          value={sliderRange}
                          onChange={handleSliderChange}
                          className="custom-golden-slider"
                        />

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 12,
                            marginTop: 2,
                          }}
                        >
                          <span>{formatNumber(sliderRange[0])}</span>
                          <span>{formatNumber(sliderRange[1])}</span>
                        </div>
                      </div>

                    // Dropdown filter
                    ) : options[colKey] ? (
                      <Select
                        isMulti
                        name={colKey}
                        options={options[colKey]}
                        value={filters[colKey]?.map((v) => ({
                          value: v,
                          label: v,
                        }))}
                        onChange={(selected) => {
                          const vals = selected ? selected.map((s) => s.value) : [];
                          handleChange({ target: { name: colKey, value: vals } });
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Filter..."
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({
                            ...base,
                            fontSize: "14px",
                            backgroundColor: "rgba(26, 26, 26, 0.98)",
                            backdropFilter: "blur(20px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: "8px",
                            overflow: "hidden",
                          }),
                          menuList: (base) => ({
                            ...base,
                            backgroundColor: "rgba(26, 26, 26, 0.98)",
                            maxHeight: 250,
                            padding: 0,
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused
                              ? "rgba(151, 0, 239, 0.2)"
                              : state.isSelected
                              ? "rgba(151, 0, 239, 0.4)"
                              : "rgba(26, 26, 26, 0.98)",
                            color: "#fff",
                            cursor: "pointer",
                            padding: "8px 12px",
                          }),
                          control: (base) => ({ ...base, minHeight: 36 }),
                        }}
                      />

                    // Filter
                    ) : (
                    <div className="dashboard-filter-input-container">
                      <input
                        type="text"
                        name={colKey}
                        value={filters[colKey]}
                        onChange={handleChange}
                        placeholder="Filter..."
                        className="dashboard-filter-input"
                        style={{
                          paddingRight: filters[colKey] ? 32 : 12, // More space for the × button
                        }}
                      />
                      {filters[colKey] && (
                        <span
                          onClick={() => handleClearFilter(colKey)}
                          className="dashboard-filter-clear"
                        >
                          ×
                        </span>
                      )}
                    </div>
                    )}
                  </th>
                );
              })}
          </tr>
          </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.filter((col) => {
                      const colKey = typeof col === 'string' ? col : col.key;
                      return visibleColumns.includes(colKey);
                    }).length}
                    className="dashboard-td"
                    style={{ textAlign: "center" }}
                  >
                    No data found.
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "dashboard-even-row" : ""}
                  >
                    {columns
                      .filter((col) => {
                        const colKey = typeof col === 'string' ? col : col.key;
                        return visibleColumns.includes(colKey);
                      })
                      .map((col) => {
                        const colKey = typeof col === 'string' ? col : col.key;
                        
                        return (
                          <td
                            key={colKey}
                            className={`dashboard-td ${
                              colKey === "project_name" ||
                              colKey === "corporate_beneficiary" ||
                              colKey === "intermediary"
                                ? "dashboard-clickable-cell"
                                : ""
                            }`}
                            style={{ width: columnWidths[colKey] }}
                            onClick={
                              colKey === "project_name"
                                ? () => handleProjectClick(row[colKey])
                                : colKey === "corporate_beneficiary"
                                ? () => handleCorporateBeneficiaryClick(row[colKey])
                                : colKey === "intermediary"
                                ? () => handleIntermediaryClick(row[colKey])
                                : undefined
                            }
                            title={row[colKey]}
                          >
                            {row[colKey]}
                          </td>
                        );
                      })}
                  </tr>
                ))
              )}
            </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="dashboard-pagination">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className={`dashboard-page-btn ${page === 1 ? "disabled" : ""}`}
        >
          Previous
        </button>

        <span style={{ color: "#fff", margin: "0 12px", fontWeight: 500 }}>
          Page {page} of {Math.ceil(total / pageSize) || 1}
        </span>

        <button
          onClick={() =>
            setPage((p) => Math.min(Math.ceil(total / pageSize), p + 1))
          }
          disabled={page === Math.ceil(total / pageSize) || total === 0}
          className={`dashboard-page-btn ${
            page === Math.ceil(total / pageSize) || total === 0 ? "disabled" : ""
          }`}
        >
          Next
        </button>
      </div>
    </>
  );
}
